
import type {
  Identifiable,
  ProductDomainService,
  QueryRanking,
  QueryRankingPinnedElementDTO,
} from '@empathyco/playboard-core-domain'
import type { PropType, SetupContext } from 'vue'
import type { QueryRankingElementActionConfig } from '../../composables/use-query-ranking-element-actions-config'
import type { ToolsMessages } from '../../models'
import type { QueryRankingDomainService } from '../../services/interfaces/domain/query-ranking-domain.service'
import type { AcceptEventPayload } from './use-query-ranking-modal'
import { PinProductAction } from '@empathyco/playboard-core-domain'
import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { EpbTextbox } from '../shared/epb-textbox'
import BaseQueryRankingModal from './base-query-ranking-modal.component.vue'
import { useQueryRankingModal } from './use-query-ranking-modal'

type PinnedProductQueryRankingModalAcceptEventPayload = QueryRankingPinnedElementDTO[]

export default defineComponent({
  name: 'PinnedProductQueryRankingModal',
  components: {
    BaseQueryRankingModal,

    EpbTextbox,
  },
  props: {
    messages: {
      type: Object as PropType<ToolsMessages['configurations']>,
      required: true,
    },
    queryRanking: {
      type: Object as PropType<QueryRanking>,
      required: true,
    },
    queryRankingConfig: {
      type: Object as PropType<QueryRankingPinnedElementDTO & Identifiable>,
      required: false,
      default: null,
    },
  },
  emits: {
    close: () => {},
    accept: (payload: PinnedProductQueryRankingModalAcceptEventPayload) => {},
  },
  setup(props, ctx: SetupContext) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()

    const MIN_POSITION = 1
    const MAX_POSITION = 24

    const queryRankingModal = useQueryRankingModal({ resetFields, buildAcceptEventPayload }, ctx)

    const isClosing = ref(false)

    const initialFieldValues = {
      productId: null,
      position: null,
    }

    const actions: Array<QueryRankingElementActionConfig['action']> = [PinProductAction]

    const productId = ref<string>(initialFieldValues.productId)
    const position = ref<number>(initialFieldValues.position)

    const productDomainService = CONTAINER.get<ProductDomainService>(TYPES.ProductDomainService)
    const qrDomainService = CONTAINER.get<QueryRankingDomainService>(
      TYPES.QueryRankingDomainService,
    )

    function handleClose(): void {
      isClosing.value = true
      queryRankingModal.onClose()
      setTimeout(() => {
        isClosing.value = false
      }, 300)
    }

    const hasConflictingItem = computed(() => {
      return qrDomainService.arePinnedProductFieldsInConflict(props.queryRanking, {
        id: props.queryRankingConfig ? props.queryRankingConfig.id : undefined,
        fieldName: qrDomainService.getProductIdField(),
        fieldValue: productId.value,
        schedule: queryRankingModal.schedule.value,
        position: undefined,
      })
    })

    const isProductIdValid = computed((): boolean => {
      return productId.value && productDomainService.isProductIdValid(productId.value)
    })

    const isPositionInUse = computed((): boolean => {
      if (!position.value) {
        return false
      }

      return qrDomainService.isPinnedProductPositionInConflict(props.queryRanking, {
        id: props.queryRankingConfig ? props.queryRankingConfig.id : undefined,
        position: position.value,
        schedule: queryRankingModal.schedule.value,
      })
    })

    const isPositionValid = computed((): boolean => {
      return (
        !!position.value &&
        position.value >= MIN_POSITION &&
        position.value <= MAX_POSITION &&
        !isPositionInUse.value
      )
    })

    const isPositionInvalid = computed((): boolean => {
      return !!position.value && (position.value < MIN_POSITION || position.value > MAX_POSITION)
    })

    const isFormValid = computed((): boolean => {
      return isProductIdValid.value && isPositionValid.value && !hasConflictingItem.value
    })

    const positionHelpMessage = computed((): string => {
      return $epb.utils.compileMessage(props.messages.modals.pinnedProducts.fields.position.info, {
        min: MIN_POSITION.toString(),
        max: MAX_POSITION.toString(),
      })
    })

    function resetFields(): void {
      if (props.queryRankingConfig) {
        productId.value = props.queryRankingConfig.fieldValue
        position.value = props.queryRankingConfig.position
      } else {
        productId.value = initialFieldValues.productId
        position.value = initialFieldValues.position
      }
    }

    function buildAcceptEventPayload(
      acceptEventPayload: AcceptEventPayload,
    ): PinnedProductQueryRankingModalAcceptEventPayload {
      return [
        qrDomainService.createPinnedProduct({
          fieldValue: productId.value,
          schedule: acceptEventPayload.schedule,
          action: PinProductAction as typeof PinProductAction,
          position: position.value,
          filters: {},
        }),
      ]
    }

    const productIdValidationAttributes = computed((): string => {
      if (isClosing.value) return ''
      if (hasConflictingItem.value) {
        return props.messages.modals.pinnedProducts.overlappedResult
      }
      if (productId.value && !isProductIdValid.value) {
        return props.messages.fields.products.invalidProductID
      }
      return ''
    })

    const positionValidationAttributes = computed((): string => {
      if (isClosing.value) return ''
      if (isPositionInvalid.value) {
        return positionHelpMessage.value
      }
      if (isPositionInUse.value) {
        return props.messages.modals.pinnedProducts.positionInUse
      }
      return ''
    })

    return {
      ...queryRankingModal,
      isFormValid,
      productId,
      position,
      positionHelpMessage,
      MIN_POSITION,
      MAX_POSITION,
      actions,
      productIdValidationAttributes,
      positionValidationAttributes,
      handleClose,
    }
  },
})
